import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled, { css } from 'styled-components';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import {
  Body,
  CheckBox as BaseCheckBox,
  ConfirmationModal as BaseConfirmationModal,
  TextArea,
  Loader,
  bodySemiBold
} from '@ubeya/shared-web/components';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { SchedulingContextDispatcher, SchedulingContextSelector } from 'scheduling/contexts/SchedulingContext';

const ConfirmationModal = styled(BaseConfirmationModal)`
  .warning-row {
    ${({ $isMultipleProjects }) =>
      $isMultipleProjects &&
      css`
        border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
      `}
    > svg {
      align-self: start;
    }
  }

  div > .loader-wrapper {
    margin-top: 20px;
    overflow: hidden;
  }
`;

const TextAreaFieldTitle = styled(Body)`
  color: ${({ theme }) => theme.colors.grey4};
  margin-bottom: 4px;
`;

const CheckBox = styled(BaseCheckBox)`
  margin-left: -20px;
  padding: 0px;
  margin-bottom: 8px;
  ${({ disabled, theme }) =>
    disabled &&
    `label { color: ${theme.colors.grey3}; } input[type="checkbox"] + label::before { background-color: ${theme.colors.grey2}; };`}
`;

const ErrorDeleteProjectDateHeader = styled.div`
  ${bodySemiBold}
  margin-bottom: 4px;
`;

const ErrorDeleteProjectLabel = styled.div`
  color: ${({ theme, $color = 'black' }) => theme.colors[$color]};
`;

const ErrorDeleteProjectSectionContainer = styled.div`
  margin-bottom: 8px;
`;

const ErrorDeleteProjectSection = ({ projectStartDate, projectName, projectLocationId, errors = [] }) => {
  const { t, shortDateFormat } = useContext(ConfigContext);
  const { mappedLocations } = useLocations();
  return (
    <ErrorDeleteProjectSectionContainer>
      <ErrorDeleteProjectDateHeader>
        {moment(projectStartDate).format(shortDateFormat + ', ddd')}
      </ErrorDeleteProjectDateHeader>
      <ErrorDeleteProjectLabel>{projectName}</ErrorDeleteProjectLabel>
      <ErrorDeleteProjectLabel $color="grey3">{mappedLocations[projectLocationId]?.name}</ErrorDeleteProjectLabel>
      {errors.map((error) => (
        <ErrorDeleteProjectLabel $color="redNegative" key={error}>
          {t(error)}
        </ErrorDeleteProjectLabel>
      ))}
    </ErrorDeleteProjectSectionContainer>
  );
};

const ProjectDeleteFormModal = ({
  onClose,
  projectIds,
  onSuccess,
  deleteFunc,
  isWeeklyPlanner = false,
  skipDeleteErrorsCheck = false
}) => {
  const { t } = useContext(ConfigContext);
  const { projectById: mappedProjects } = useContext(SchedulingContextSelector);
  const {
    deleteProject,
    deleteProjectsBulk,
    isLoadingDeleteProjectsBulk,
    isLoadingDeleteProject,
    getDeleteProjectErrors,
    isLoadingGetDeleteProjectErrors
  } = useContext(SchedulingContextDispatcher);

  const isMultipleProjects = projectIds.length > 1;

  const [errors, setErrors] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reasonDeleteProject, setReasonDeleteProject] = useState('');
  const [confirmDeleteWithBookedEmployees, setConfirmDeleteWithBookedEmployees] = useState(false);
  const { addResponseError } = useToaster();

  useEffect(() => {
    if (projectIds.length === 1 && !skipDeleteErrorsCheck) {
      getDeleteProjectErrors({ projectId: projectIds[0] }).catch((error) => {
        const deleteProjectErrors = error?.response?.data?.errors;
        if (deleteProjectErrors?.length) {
          const formattedErrors = deleteProjectErrors.map((deleteProjectError) => (
            <span data-testid="delete-event-block-reason-text" key={deleteProjectError}>
              {t(deleteProjectError)}
            </span>
          ));
          setErrors(formattedErrors);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasBookedEmployees = useMemo(
    () =>
      projectIds.some((projectId) => {
        const project = mappedProjects[projectId];
        const projectDates = Object.values(project?.dates || {});
        return projectDates.some(({ stats = {} } = {}) => stats.booked);
      }, 0),
    [mappedProjects, projectIds]
  );

  //Support delete multiple projects
  const handleDeleteProjects = useCallback(async () => {
    try {
      if (deleteFunc) {
        await deleteFunc();
      } else if (isMultipleProjects) {
        const { data } = await deleteProjectsBulk({
          projectIds,
          message: reasonDeleteProject
        });

        const projectsErrors = Object.entries(data);

        if (projectsErrors.length) {
          const formattedErrors = [];

          for (const [projectId, projectDeletionErrors] of projectsErrors) {
            // Build the error modal content
            const project = mappedProjects[projectId];
            if (!project) {
              continue; // Skip if project not found
            }
            formattedErrors.push(
              <ErrorDeleteProjectSection
                projectStartDate={project.date}
                projectName={project?.name}
                projectLocationId={project.locationId}
                errors={projectDeletionErrors}
              />
            );
          }

          setErrors(formattedErrors);
          return;
        }
      } else {
        const [projectId] = projectIds;
        await deleteProject({ message: reasonDeleteProject, projectId });
      }

      onClose();
      onSuccess?.();
    } catch (error) {
      // We get here only if delete single project failed
      const deleteProjectErrors = error?.response?.data?.errors;

      if (deleteProjectErrors?.length) {
        const formattedErrors = deleteProjectErrors.map((deleteProjectError) => (
          <span data-testid="delete-event-block-reason-text" key={deleteProjectError}>
            {t(deleteProjectError)}
          </span>
        ));
        setErrors(formattedErrors);
        return;
      }

      addResponseError(error);
    }
  }, [
    deleteFunc,
    isMultipleProjects,
    onClose,
    onSuccess,
    deleteProjectsBulk,
    projectIds,
    reasonDeleteProject,
    mappedProjects,
    deleteProject,
    addResponseError,
    t
  ]);

  const isLoadingDelete = isLoadingDeleteProjectsBulk || isLoadingDeleteProject;

  // canDelete is true when the following conditions are met:
  // 1. User has confirmed deletion (confirmDelete is true)
  // 2. Either:
  //    - There are no booked employees (totalBooked === 0), or
  //    - User has confirmed deletion with booked employees (confirmDeleteWithBookedEmployees is true)

  const canDelete = confirmDelete && (!hasBookedEmployees || confirmDeleteWithBookedEmployees);

  const title = isLoadingGetDeleteProjectErrors
    ? t(isWeeklyPlanner ? 'checkingWeeklyPlannerDeletion' : 'checkingEventDeletion')
    : isMultipleProjects
    ? t(isWeeklyPlanner ? 'deleteWeeklyPlanners' : 'deleteProjects')
    : t(isWeeklyPlanner ? 'deleteWeeklyPlanner' : 'deleteProject');

  const confirmDeleteLabel = isWeeklyPlanner
    ? t(isMultipleProjects ? 'confirmDeleteWeeklyPlanners' : 'confirmDeleteWeeklyPlanner')
    : t(isMultipleProjects ? 'confirmDeleteProjects' : 'confirmDeleteProject');

  return (
    <ConfirmationModal
      title={title}
      confirmText="delete"
      loadingConfirm={isLoadingDelete}
      disabledConfirm={!canDelete}
      errors={errors}
      onConfirm={handleDeleteProjects}
      onClose={onClose}
      dataTestId="delete-event-modal"
      $isMultipleProjects={isMultipleProjects}
      confirmationModalWrapperStyle={{ zIndex: 101 }}
      showCancelButton={!isLoadingGetDeleteProjectErrors}
      showConfirmButton={!isLoadingGetDeleteProjectErrors}
      isPermanentAction
      usePortal>
      {isLoadingGetDeleteProjectErrors ? (
        <Loader />
      ) : (
        <>
          <CheckBox
            name="confirmDelete"
            onChange={() => setConfirmDelete(!confirmDelete)}
            value={confirmDelete}
            label={confirmDeleteLabel}
            dataTestId="delete-event-confirm-checkbox"
          />
          {hasBookedEmployees && (
            <>
              <CheckBox
                onChange={() => setConfirmDeleteWithBookedEmployees(!confirmDeleteWithBookedEmployees)}
                value={confirmDeleteWithBookedEmployees}
                label={t('confirmDeleteProjectWithBookedEmployees')}
                name="confirmDeleteWithBookedEmployees"
                dataTestId="delete-event-booked-employees-checkbox"
              />
              {confirmDeleteWithBookedEmployees && (
                <>
                  <TextAreaFieldTitle>{t('reasonDeleteProjectNewPopUp')}</TextAreaFieldTitle>
                  <TextArea
                    onChange={setReasonDeleteProject}
                    value={reasonDeleteProject}
                    minRows={3}
                    placeholder={t('addText')}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
    </ConfirmationModal>
  );
};

export default ProjectDeleteFormModal;
